import React, { useMemo } from 'react';
import {
  Alert,
  Card,
  Empty,
  Space,
  Spin,
  Table,
  Tag,
  Tooltip,
  Typography,
} from 'antd';
import { ClockCircleOutlined, ReloadOutlined, ScheduleOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { formatDate as formatLocaleDate } from '../../i18n';
import { Button } from 'antd';
import { useListSimEventsQuery } from '../../api/simulationApi';
import type { Save } from '../../types/saves';
import type { SimEvent } from '../../types/simulation';

const { Text, Paragraph } = Typography;

interface SaveEventsPanelProps {
  save: Save;
}

const formatDate = (iso?: string | null) => (iso ? formatLocaleDate(iso, 'short') : '—');

const targetCell = (event: SimEvent) => {
  const scenarioTarget = event.payload?.scenario_target as { kind?: string; id?: string } | undefined;
  const target = scenarioTarget?.kind && scenarioTarget?.id
    ? { kind: scenarioTarget.kind, id: String(scenarioTarget.id) }
    : event.plantingId || event.planting_id
      ? { kind: 'planting', id: String(event.plantingId || event.planting_id) }
      : event.bedId || event.bed_id
        ? { kind: 'bed', id: String(event.bedId || event.bed_id) }
        : event.plotId || event.plot_id
          ? { kind: 'plot', id: String(event.plotId || event.plot_id) }
          : event.fieldId || event.field_id
            ? { kind: 'field', id: String(event.fieldId || event.field_id) }
            : event.structuralObjectId || event.structural_object_id
            ? { kind: 'structural_object', id: String(event.structuralObjectId || event.structural_object_id) }
            : null;
  if (!target) return <Text type="secondary">—</Text>;
  return (
    <Space size={4}>
      <Tag>{target.kind}</Tag>
      <Text code copyable={{ text: target.id }}>
        {target.id.slice(0, 8)}…
      </Text>
    </Space>
  );
};

export const SaveEventsPanel: React.FC<SaveEventsPanelProps> = ({ save }) => {
  const { t } = useTranslation('saves');
  const { data, isLoading, isError, refetch } = useListSimEventsQuery(
    { saveId: save.id, sessionId: save.simSessionId || undefined }
  );

  const events = useMemo(() => data?.events || [], [data]);

  return (
    <Card
      size="small"
      title={
        <Space>
          <ScheduleOutlined /> {t('events.title')}
          {events.length > 0 ? <Tag>{events.length}</Tag> : null}
        </Space>
      }
      extra={
        <Button size="small" icon={<ReloadOutlined />} onClick={() => refetch()}>
          {t('common:actions.refresh')}
        </Button>
      }
      style={{ marginBottom: 16 }}
    >
      <Paragraph type="secondary" style={{ marginBottom: 12 }}>
        {t('events.description')}
      </Paragraph>
      {isLoading ? (
        <div style={{ textAlign: 'center', padding: 16 }}>
          <Spin />
        </div>
      ) : isError ? (
        <Alert type="error" showIcon message={t('events.loadFailed')} />
      ) : events.length === 0 ? (
        <Empty description={t('events.empty')} image={Empty.PRESENTED_IMAGE_SIMPLE} />
      ) : (
        <Table
          size="small"
          dataSource={events}
          rowKey={(e) => e.id}
          pagination={{ pageSize: 20, hideOnSinglePage: true }}
          columns={[
            {
              title: <Tooltip title={t('events.dateTooltip')}>{t('common:labels.date')}</Tooltip>,
              dataIndex: 'date',
              key: 'date',
              width: 120,
              render: (v: string) => (
                <Space size={4}>
                  <ClockCircleOutlined />
                  <span>{formatDate(v)}</span>
                </Space>
              ),
            },
            {
              title: 'Action',
              dataIndex: 'action',
              key: 'action',
              render: (v: string) => <Tag color="blue">{v}</Tag>,
            },
            {
              title: t('events.target'),
              key: 'target',
              render: (_: unknown, ev) => targetCell(ev),
            },
            {
              title: 'Status',
              dataIndex: 'status',
              key: 'status',
              width: 110,
              render: (v: string) => <Tag>{v || '—'}</Tag>,
            },
            {
              title: 'Notes',
              dataIndex: 'notes',
              key: 'notes',
              render: (v: string | null) => v || <Text type="secondary">—</Text>,
            },
          ]}
        />
      )}
    </Card>
  );
};

export default SaveEventsPanel;
